import React, { useEffect, useRef, useState } from 'react';
import './AgentCards.css';

const agents = [
  {
    id: 'memo',
    name: 'MEMO',
    full: 'Meeting Extraction & Monitoring Orchestration',
    desc: 'Transforms raw meeting transcripts into structured intelligence — extracts decisions, action items, assigns owners, and escalates blockers automatically.',
    howItWorks: 'Paste or upload a meeting transcript. 6 agents process it sequentially: parsing the conversation, extracting decisions and action items, then prioritizing tasks, assigning owners, and flagging escalations.',
    useCases: ['Engineering standups & sprint planning', 'Board meeting minutes extraction', 'Client call action-item tracking', 'Cross-team decision audit trails'],
    agents: ['TranscriptAnalyzer', 'DecisionExtractor', 'ActionItemExtractor', 'TaskPrioritizer', 'OwnerAssigner', 'EscalationDecider'],
    color: '#3b82f6',
    icon: '🎙️',
    url: 'https://memo.thanush.site',
    github: 'https://github.com/Thanush-41/MEMO-Meeting-Extraction-Monitoring-Orchestration',
    tags: ['NLP', 'Meetings', 'Action Items'],
  },
  {
    id: 'legalex',
    name: 'LEGALEX',
    full: 'Legal Document Intelligence',
    desc: 'Parses complex legal contracts and agreements to extract obligations, deadlines, compliance risks, and assigns legal review tasks with escalation protocols.',
    howItWorks: 'Submit a contract or legal document. Agents parse clauses, extract obligations and deadlines, flag compliance risks, then prioritize review tasks and assign responsible parties.',
    useCases: ['SaaS agreement obligation tracking', 'NDA risk clause identification', 'Vendor contract compliance audits', 'Enterprise license renewal management'],
    agents: ['ContractParser', 'ObligationExtractor', 'DecisionExtractor', 'TaskPrioritizer', 'OwnerAssigner', 'EscalationDecider'],
    color: '#10b981',
    icon: '⚖️',
    url: 'https://legalex.thanush.site',
    github: 'https://github.com/ARIA-AutonomousResolution-Intelligence/legalex',
    tags: ['Legal', 'Contracts', 'Compliance'],
  },
  {
    id: 'supportiq',
    name: 'SUPPORTIQ',
    full: 'Customer Support Intelligence',
    desc: 'Analyzes support tickets to extract customer needs, detect sentiment, identify churn risk, and auto-escalate critical issues based on SLA and customer tier.',
    howItWorks: 'Feed in a support ticket with customer tier info. Agents analyze the ticket content, extract specific needs, gauge sentiment and frustration level, then prioritize response and escalate SLA-critical issues.',
    useCases: ['Enterprise ticket triage & routing', 'Churn risk early detection', 'SLA violation prevention', 'Customer sentiment trend analysis'],
    agents: ['TicketAnalyzer', 'NeedExtractor', 'SentimentAnalyzer', 'TaskPrioritizer', 'OwnerAssigner', 'EscalationDecider'],
    color: '#38bdf8',
    icon: '🎧',
    url: 'https://supportiq.thanush.site',
    github: 'https://github.com/ARIA-AutonomousResolution-Intelligence/supportiq',
    tags: ['Support', 'Sentiment', 'SLA'],
  },
  {
    id: 'postmortem',
    name: 'POSTMORTEM',
    full: 'Incident Analysis Intelligence',
    desc: 'Processes post-incident reports to build timelines, identify root causes, extract remediation action items, and track resolution ownership across teams.',
    howItWorks: 'Submit an incident report or post-mortem document. Agents parse the incident, reconstruct the timeline, extract remediation actions, then prioritize fixes and assign follow-up owners.',
    useCases: ['SEV1/SEV2 post-incident reviews', 'Root cause analysis automation', 'Remediation tracking across SRE teams', 'Incident pattern detection over time'],
    agents: ['IncidentParser', 'TimelineExtractor', 'ActionExtractor', 'TaskPrioritizer', 'OwnerAssigner', 'EscalationDecider'],
    color: '#f97316',
    icon: '🔥',
    url: 'https://postmortem.thanush.site',
    github: 'https://github.com/ARIA-AutonomousResolution-Intelligence/postmortem',
    tags: ['Incidents', 'Root Cause', 'SRE'],
  },
  {
    id: 'coderev',
    name: 'CODEREV',
    full: 'Code Review Intelligence',
    desc: 'Analyzes pull requests and code review comments to flag security vulnerabilities, detect breaking changes, prioritize review feedback, and assign reviewers.',
    howItWorks: 'Paste a PR diff or review comments. Agents analyze the code changes, extract reviewer comments and concerns, detect security issues and breaking changes, then prioritize and assign code review actions.',
    useCases: ['Automated security vulnerability flagging', 'Breaking change detection in PRs', 'Code review workload balancing', 'Engineering quality metrics tracking'],
    agents: ['PRAnalyzer', 'CommentExtractor', 'ActionExtractor', 'TaskPrioritizer', 'OwnerAssigner', 'EscalationDecider'],
    color: '#a78bfa',
    icon: '🔍',
    url: 'https://coderev.thanush.site',
    github: 'https://github.com/ARIA-AutonomousResolution-Intelligence/coderev',
    tags: ['Code Review', 'Security', 'PRs'],
  },
  {
    id: 'earningsiq',
    name: 'EARNINGSIQ',
    full: 'Earnings Call Intelligence',
    desc: 'Parses quarterly earnings call transcripts to extract financial guidance, management commitments, risk signals, and generate analyst-ready intelligence briefs.',
    howItWorks: 'Upload an earnings call transcript. Agents parse the Q&A and prepared remarks, extract management commitments and forward guidance, analyze financial metrics, then prioritize analyst follow-up items.',
    useCases: ['Quarterly earnings analysis automation', 'Management commitment tracking', 'Guidance change detection (raised/lowered)', 'Investment risk signal identification'],
    agents: ['TranscriptParser', 'CommitmentExtractor', 'GuidanceAnalyzer', 'TaskPrioritizer', 'OwnerAssigner', 'EscalationDecider'],
    color: '#22d3ee',
    icon: '📊',
    url: 'https://earningsiq.thanush.site',
    github: 'https://github.com/ARIA-AutonomousResolution-Intelligence/earningsiq',
    tags: ['Finance', 'Earnings', 'Analysis'],
  },
  {
    id: 'hrflow',
    name: 'HRFLOW',
    full: 'HR Performance Intelligence',
    desc: 'Processes performance reviews to extract development goals, analyze feedback patterns, identify promotion-ready talent, and flag employees needing support plans.',
    howItWorks: 'Submit a performance review document. Agents parse the review, extract goals and development areas, analyze feedback sentiment and patterns, then prioritize HR actions and flag promotion or PIP candidates.',
    useCases: ['Annual review cycle analysis', 'Promotion readiness identification', 'PIP (Performance Improvement Plan) detection', 'Team-wide development goal tracking'],
    agents: ['ReviewParser', 'GoalExtractor', 'FeedbackAnalyzer', 'TaskPrioritizer', 'OwnerAssigner', 'EscalationDecider'],
    color: '#ec4899',
    icon: '👥',
    url: 'https://hrflow.thanush.site',
    github: 'https://github.com/ARIA-AutonomousResolution-Intelligence/hrflow',
    tags: ['HR', 'Performance', 'Talent'],
  },
  {
    id: 'intelbrief',
    name: 'INTELBRIEF',
    full: 'Intelligence Briefing Analysis',
    desc: 'Analyzes geopolitical and strategic intelligence reports to extract key events, assess impact on stakeholders, map threat landscapes, and escalate critical findings.',
    howItWorks: 'Paste an intelligence report or news analysis. Agents parse the article, extract key geopolitical events, assess impact on stakeholders and markets, then prioritize response actions and escalate high-impact findings.',
    useCases: ['Geopolitical risk monitoring', 'Supply chain threat assessment', 'Regulatory impact analysis', 'Strategic intelligence dissemination'],
    agents: ['ArticleParser', 'EventExtractor', 'ImpactAnalyzer', 'TaskPrioritizer', 'OwnerAssigner', 'EscalationDecider'],
    color: '#10b981',
    icon: '🌐',
    url: 'https://intelbrief.thanush.site',
    github: 'https://github.com/ARIA-AutonomousResolution-Intelligence/intelbrief',
    tags: ['Intelligence', 'Geopolitical', 'Risk'],
  },
];

function AgentCard({ agent, index }) {
  const ref = useRef(null);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      className="agent-card"
      ref={ref}
      style={{ '--card-accent': agent.color, '--card-delay': `${index * 0.08}s` }}
    >
      <div className="card-glow" />
      <div className="card-header">
        <div className="card-icon-wrap">
          <span className="card-icon">{agent.icon}</span>
        </div>
        <div>
          <h3 className="card-name">{agent.name}</h3>
          <p className="card-full">{agent.full}</p>
        </div>
      </div>
      <p className="card-desc">{agent.desc}</p>
      <div className="card-how">
        <span className="card-how-icon">⚡</span>
        <span className="card-how-text">{agent.howItWorks}</span>
      </div>
      <div className="card-usecases">
        <span className="card-agents-label">Use Cases</span>
        <div className="usecase-list">
          {agent.useCases.map((uc) => (
            <span className="usecase-chip" key={uc}>✦ {uc}</span>
          ))}
        </div>
      </div>
      <button className={`card-expand-btn ${expanded ? 'open' : ''}`} onClick={() => setExpanded(!expanded)}>
        <span>{expanded ? 'Hide' : 'Show'} Agent Pipeline</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </button>
      <div className={`card-agents-collapsible ${expanded ? 'open' : ''}`}>
        <div className="card-agents">
          <div className="agent-flow">
            {agent.agents.map((a, i) => (
              <React.Fragment key={a}>
                <span className="agent-chip">{a}</span>
                {i < agent.agents.length - 1 && <span className="agent-arrow">→</span>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
      <div className="card-tags">
        {agent.tags.map((t) => (
          <span className="card-tag" key={t}>{t}</span>
        ))}
      </div>
      <div className="card-footer">
        <a href={agent.url} target="_blank" rel="noopener noreferrer" className="card-btn card-btn-primary">
          Open Dashboard
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/></svg>
        </a>
        <a href={agent.github} target="_blank" rel="noopener noreferrer" className="card-btn card-btn-outline">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.37 0 0 5.37 0 12c0 5.31 3.435 9.795 8.205 11.385.6.105.825-.255.825-.57 0-.285-.015-1.23-.015-2.235-3.015.555-3.795-.735-4.035-1.41-.135-.345-.72-1.41-1.23-1.695-.42-.225-1.02-.78-.015-.795.945-.015 1.62.87 1.845 1.23 1.08 1.815 2.805 1.305 3.495.99.105-.78.42-1.305.765-1.605-2.67-.3-5.46-1.335-5.46-5.925 0-1.305.465-2.385 1.23-3.225-.12-.3-.54-1.53.12-3.18 0 0 1.005-.315 3.3 1.23.96-.27 1.98-.405 3-.405s2.04.135 3 .405c2.295-1.56 3.3-1.23 3.3-1.23.66 1.65.24 2.88.12 3.18.765.84 1.23 1.905 1.23 3.225 0 4.605-2.805 5.625-5.475 5.925.435.375.81 1.095.81 2.22 0 1.605-.015 2.895-.015 3.3 0 .315.225.69.825.57A12.02 12.02 0 0024 12c0-6.63-5.37-12-12-12z"/></svg>
          Source
        </a>
      </div>
    </div>
  );
}

export default function AgentCards() {
  return (
    <section className="agents-section" id="agents">
      <div className="section-container">
        <div className="section-header">
          <span className="section-badge">Agent Systems</span>
          <h2 className="section-title">8 Autonomous Agent Systems</h2>
          <p className="section-desc">
            Each system deploys 6 specialized agents — 3 extraction and 3 decision — 
            working together through intelligent orchestration to transform unstructured 
            data into actionable intelligence.
          </p>
        </div>
        <div className="agents-grid">
          {agents.map((agent, i) => (
            <AgentCard key={agent.id} agent={agent} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
